import { useEffect, useState, type ReactNode } from 'react';
import { useI18n, useT } from '../i18n.tsx';
import type { Locale } from '@hitster/shared';

/** Token-Anzeige. Verbrauchte Plaetze bleiben als leere Ringe sichtbar. */
export function Tokens({ count, max }: { count: number; max: number }) {
  const t = useT();
  return (
    <span className="tokens" title={`${t('game.tokens')}: ${count}/${max}`}>
      {Array.from({ length: Math.max(count, Math.min(max, 3)) }, (_, index) => (
        <span key={index} className={`token ${index < count ? '' : 'token--spent'}`} />
      ))}
      {count > 0 && <span className="text-xs text-muted">{count}</span>}
    </span>
  );
}

export function Alert({
  kind,
  children,
}: {
  kind: 'error' | 'warning' | 'info' | 'success';
  children: ReactNode;
}) {
  return (
    <div className={`alert alert--${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      <span>{children}</span>
    </div>
  );
}

export function Progress({ value, max }: { value: number; max: number }) {
  const percent = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div
      className="progress"
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
    >
      <div className="progress__bar" style={{ width: `${percent}%` }} />
    </div>
  );
}

/**
 * Countdown bis zu einem Zeitstempel des Servers.
 *
 * Tickt nur jede halbe Sekunde -- haeufiger bringt optisch nichts und
 * kostet auf Handys unnoetig Akku.
 */
export function Countdown({ deadline }: { deadline: number | null }) {
  const [remaining, setRemaining] = useState(() =>
    deadline === null ? 0 : Math.max(0, deadline - Date.now()),
  );

  useEffect(() => {
    if (deadline === null) return;

    const tick = () => setRemaining(Math.max(0, deadline - Date.now()));
    tick();
    const interval = setInterval(tick, 500);
    return () => clearInterval(interval);
  }, [deadline]);

  if (deadline === null) return null;

  const seconds = Math.ceil(remaining / 1000);
  return (
    <span className={`countdown ${seconds <= 5 ? 'countdown--urgent' : ''}`}>⏱ {seconds}s</span>
  );
}

export function LanguageSwitch() {
  const { locale, setLocale } = useI18n();

  const options: { value: Locale; label: string }[] = [
    { value: 'de', label: 'DE' },
    { value: 'en', label: 'EN' },
  ];

  return (
    <div className="row" style={{ gap: '0.25rem' }} role="group" aria-label="Sprache / Language">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="btn btn--ghost"
          onClick={() => setLocale(option.value)}
          aria-pressed={locale === option.value}
          style={
            locale === option.value
              ? { color: 'var(--accent)', fontWeight: 800 }
              : undefined
          }
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Spinner() {
  return <span className="spinner" aria-hidden="true" />;
}
